import { useEffect, useMemo, useRef, useState } from "react";
import {
  clearStoredAiSession,
  createAiSession,
  getAiRuntimeHealth,
  getAiSession,
  getStoredAiSessionUuid,
  requestAiHandoff,
  sendAiChatMessage,
  synthesizeAiVoice,
  transcribeAiVoice,
} from "../../api/aiAssistant.js";
import "./HomevilasAssistantWidget.css";

const ENABLED = process.env.REACT_APP_AI_ASSISTANT_ENABLED === "true";
const VOICE_ENABLED = process.env.REACT_APP_AI_VOICE_ENABLED !== "false";
const VOICE_MODE = process.env.REACT_APP_AI_VOICE_MODE || "browser";
const WELCOME_MESSAGE = {
  id: "welcome",
  sender: "assistant",
  text: "Namaste. Destination, dates aur guests bataiye. Main options dhoondhunga, property select karunga aur booking steps complete karwa dunga.",
};

const formatMoney = (amount, currency = "INR") => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "Live price";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch (_error) {
    return `${currency} ${Math.round(value).toLocaleString("en-IN")}`;
  }
};

const detectSpeechLanguage = (text = "") => {
  const value = String(text).toLowerCase();
  if (/[\u0900-\u097f]/.test(value)) return "hi-IN";
  if (/\b(kya|hai|haan|nahi|dikhao|karo|bhejo|batao|chahiye|rahegi|karunga|dijiye|kripya)\b/.test(value)) {
    return "hi-IN";
  }
  return "en-IN";
};

const MessageBubble = ({ message }) => {
  const mine = message.sender === "user";
  return (
    <div className={`hv-ai-message ${mine ? "hv-ai-message-user" : "hv-ai-message-assistant"}`}>
      {message.text}
    </div>
  );
};

const PropertyCard = ({ property, onSelect }) => {
  const pricing = property.pricing || {};
  return (
    <article className="hv-ai-property-card">
      <div className="hv-ai-property-media">
        {property.image ? <img src={property.image} alt={property.name || "Homevilas stay"} loading="lazy" /> : null}
      </div>
      <div className="hv-ai-property-body">
        <div>
          <h3>{property.name}</h3>
          <p>{property.location || "Homevilas stay"}</p>
        </div>
        <div className="hv-ai-property-meta">
          {property.bedrooms ? <span>{property.bedrooms} BR</span> : null}
          {property.maxGuests ? <span>{property.maxGuests} guests</span> : null}
          {property.rating ? <span>{property.rating} rating</span> : null}
        </div>
        <div className="hv-ai-property-price">
          <strong>{formatMoney(pricing.totalStayAmount, property.currency)}</strong>
          <span>{pricing.label || "TOTAL STAY"}</span>
        </div>
        <div className="hv-ai-property-actions">
          {property.detailPath ? (
            <a href={property.detailPath}>View</a>
          ) : null}
          <button type="button" data-property-id={property.id} onClick={() => onSelect(property)}>Book</button>
        </div>
      </div>
    </article>
  );
};

export default function HomevilasAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionUuid, setSessionUuid] = useState("");
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [properties, setProperties] = useState([]);
  const [paymentAction, setPaymentAction] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceReplyUrl, setVoiceReplyUrl] = useState("");
  const [error, setError] = useState("");
  const [runtimeHealth, setRuntimeHealth] = useState(null);
  const [runtimeChecked, setRuntimeChecked] = useState(false);
  const bottomRef = useRef(null);
  const recorderRef = useRef(null);
  const recognitionRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const voiceReplyRef = useRef("");

  const aiOffline =
    runtimeChecked &&
    runtimeHealth?.provider === "ollama" &&
    (!runtimeHealth?.ollamaReachable || !runtimeHealth?.modelInstalled);
  const runtimeTitle = runtimeHealth
    ? `AI Provider: ${runtimeHealth.provider || "unknown"} | Model: ${runtimeHealth.model || "none"} | Runtime: ${
        runtimeHealth.ollamaReachable && runtimeHealth.modelInstalled ? "ONLINE" : "OFFLINE"
      } | Fallback: ${runtimeHealth.fallbackEnabled ? "ACTIVE" : "INACTIVE"}`
    : "AI runtime status unavailable";
  const canSend = useMemo(() => input.trim() && !loading && !aiOffline, [input, loading, aiOffline]);
  const SpeechRecognition =
    typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
  const hasBrowserSpeechRecognition = Boolean(SpeechRecognition);
  const canUseBrowserVoice = VOICE_ENABLED && VOICE_MODE === "browser";
  const canUseServerVoice =
    VOICE_ENABLED &&
    VOICE_MODE !== "browser" &&
    typeof navigator !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);
  const canUseVoice = canUseBrowserVoice || canUseServerVoice;

  useEffect(() => {
    if (!ENABLED || !open) return;
    let cancelled = false;
    const checkRuntime = async () => {
      setRuntimeChecked(false);
      try {
        const health = await getAiRuntimeHealth();
        if (!cancelled) setRuntimeHealth(health || null);
      } catch (_err) {
        if (!cancelled) setRuntimeHealth(null);
      } finally {
        if (!cancelled) setRuntimeChecked(true);
      }
    };
    checkRuntime();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!ENABLED || !open || sessionUuid || !runtimeChecked || aiOffline) return;
    let cancelled = false;
    const hydrate = async () => {
      setError("");
      try {
        const existing = getStoredAiSessionUuid();
        if (existing) {
          const payload = await getAiSession(existing);
          if (!cancelled && payload?.session) {
            setSessionUuid(payload.session.session_uuid || payload.session.sessionUuid);
            const history = (payload.messages || [])
              .filter((item) => item.sender_type === "user" || item.sender_type === "assistant")
              .map((item) => ({
                id: item.id,
                sender: item.sender_type === "user" ? "user" : "assistant",
                text: item.content,
              }));
            if (history.length) setMessages(history);
            return;
          }
        }
        const session = await createAiSession();
        if (!cancelled) setSessionUuid(session?.session_uuid || session?.sessionUuid || "");
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err.message || "AI assistant unavailable");
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [open, sessionUuid, runtimeChecked, aiOffline]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, properties, open]);

  useEffect(() => () => {
    recognitionRef.current?.abort?.();
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    if (voiceReplyRef.current) URL.revokeObjectURL(voiceReplyRef.current);
  }, []);

  if (!ENABLED) return null;

  const appendMessage = (message) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-${prev.length}`, ...message }]);
  };

  const setVoiceReply = (blob) => {
    if (voiceReplyRef.current) URL.revokeObjectURL(voiceReplyRef.current);
    const url = URL.createObjectURL(blob);
    voiceReplyRef.current = url;
    setVoiceReplyUrl(url);
  };

  const speakReply = async (text) => {
    if (!VOICE_ENABLED || !text) return;
    if (VOICE_MODE === "browser" && typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = detectSpeechLanguage(text);
      window.speechSynthesis.speak(utterance);
      return;
    }
    try {
      const audio = await synthesizeAiVoice(text);
      setVoiceReply(audio);
    } catch (_voiceError) {
      // Text reply remains available when TTS is disabled or unavailable.
    }
  };

  const sendTextMessage = async (text, options = {}) => {
    if (!text || loading || aiOffline) return;
    appendMessage({ sender: "user", text });
    setLoading(true);
    setError("");
    try {
      const activeSessionUuid = sessionUuid || (await createAiSession()).session_uuid;
      if (!sessionUuid) setSessionUuid(activeSessionUuid);
      const result = await sendAiChatMessage(activeSessionUuid, text, {
        messageType: options.messageType || "text",
        structuredData: options.structuredData || {},
      });
      const reply = result.reply || "Main check kar raha hoon.";
      appendMessage({ sender: "assistant", text: reply });
      const recommendations = result.toolResult?.result?.recommendations || [];
      const nextPayment =
        result.toolResult?.result?.checkoutPath || result.toolResult?.result?.payment?.checkoutPath
          ? result.toolResult.result
          : result.session?.payment?.checkoutPath
            ? result.session.payment
            : null;
      if (recommendations.length) {
        setProperties(recommendations);
        setPaymentAction(null);
      } else if (result.session?.current_stage !== "RESULTS_SHOWN" && result.session?.currentStage !== "RESULTS_SHOWN") {
        setProperties([]);
      }
      if (nextPayment?.checkoutPath) setPaymentAction(nextPayment);
      if (options.speakReply) await speakReply(reply);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Unable to send message");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    await sendTextMessage(text);
  };

  const bookProperty = async (property) => {
    if (!property?.id || loading) return;
    await sendTextMessage(`Book ${property.name || property.id}`, {
      structuredData: {
        action: "bookProperty",
        selectedPropertyId: property.id,
        property,
      },
    });
  };

  const stopRecording = () => {
    if (!recording) return;
    if (canUseBrowserVoice && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    if (recorderRef.current) recorderRef.current.stop();
  };

  const startBrowserVoice = () => {
    if (loading || recording) return;
    if (!hasBrowserSpeechRecognition) {
      setError("Voice Firefox me supported nahi hai. Chrome ya Edge me mic use kijiye.");
      return;
    }
    setError("");
    const recognition = new SpeechRecognition();
    recognition.lang = detectSpeechLanguage(input || messages[messages.length - 1]?.text || navigator.language || "");
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    recognition.onresult = async (event) => {
      const text = String(event.results?.[0]?.[0]?.transcript || "").trim();
      setRecording(false);
      recognitionRef.current = null;
      if (!text) {
        setError("Voice samajh nahi aayi. Kripya phir se try karein.");
        return;
      }
      await sendTextMessage(text, { messageType: "voice", speakReply: true });
    };
    recognition.onerror = (event) => {
      setRecording(false);
      recognitionRef.current = null;
      setError(event.error === "not-allowed" ? "Microphone permission failed" : "Voice message failed");
    };
    recognition.onend = () => {
      setRecording(false);
      recognitionRef.current = null;
    };
    recognition.onstart = () => {
      setRecording(true);
      setError("");
    };
    try {
      recognition.start();
    } catch (err) {
      setRecording(false);
      recognitionRef.current = null;
      setError(err.message || "Voice start nahi ho payi. Chrome ya Edge me try kijiye.");
    }
  };

  const startRecording = async () => {
    if (!canUseVoice || loading || recording || aiOffline) return;
    if (canUseBrowserVoice) {
      startBrowserVoice();
      return;
    }
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        setRecording(false);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        if (!audioBlob.size) return;
        setLoading(true);
        try {
          const transcript = await transcribeAiVoice(audioBlob, { filename: "homevilas-voice.webm" });
          const text = String(transcript.text || transcript.transcript || "").trim();
          if (!text) {
            setError("Voice samajh nahi aayi. Kripya phir se try karein.");
            return;
          }
          setLoading(false);
          await sendTextMessage(text, { messageType: "voice", speakReply: true });
        } catch (err) {
          setError(err?.response?.data?.message || err.message || "Voice message failed");
          setLoading(false);
        }
      };
      recorder.start();
      setRecording(true);
    } catch (err) {
      setError(err.message || "Microphone permission failed");
    }
  };

  const handoff = async () => {
    if (!sessionUuid || loading) return;
    setLoading(true);
    setError("");
    try {
      await requestAiHandoff(sessionUuid, "Customer requested human help from chat widget");
      appendMessage({
        sender: "assistant",
        text: "Homevilas team ko handoff request bhej di gayi hai.",
      });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Unable to request handoff");
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = () => {
    clearStoredAiSession();
    setSessionUuid("");
    setMessages([WELCOME_MESSAGE]);
    setProperties([]);
    setPaymentAction(null);
    setInput("");
    setError("");
    setVoiceReplyUrl("");
  };

  return (
    <section className={`hv-ai-widget ${open ? "hv-ai-widget-open" : ""}`} aria-label="Homevilas travel concierge">
      {open ? (
        <div className="hv-ai-panel">
          <header className="hv-ai-header">
            <div>
              <p>Homevilas Saathi</p>
              <strong>Travel Concierge</strong>
            </div>
            <div className="hv-ai-header-actions">
              <button
                type="button"
                className="hv-ai-agent-action"
                aria-label="Request human agent"
                title="Human agent"
                onClick={handoff}
                disabled={!sessionUuid || loading}
              >
                Agent
              </button>
              <button type="button" aria-label="Start new chat" title="New chat" onClick={startNewChat}>
                <i className="bi bi-arrow-clockwise" aria-hidden="true" />
              </button>
              <button type="button" aria-label="Close assistant" title="Close" onClick={() => setOpen(false)}>
                x
              </button>
            </div>
          </header>
          <div className="hv-ai-body" title={runtimeTitle}>
            {messages.map((message) => <MessageBubble key={message.id} message={message} />)}
            {aiOffline ? (
              <div className="hv-ai-runtime-offline">
                AI Assistant temporarily unavailable. Normal property search and support are still available.
              </div>
            ) : null}
            {properties.length ? (
              <div className="hv-ai-properties">
                {properties.map((property) => <PropertyCard key={property.id} property={property} onSelect={bookProperty} />)}
              </div>
            ) : null}
            {paymentAction?.checkoutPath ? (
              <a className="hv-ai-payment-link" href={paymentAction.checkoutPath}>
                Continue to payment
              </a>
            ) : null}
            {recording ? <div className="hv-ai-status hv-ai-listening">Listening...</div> : null}
            {loading ? <div className="hv-ai-status">Checking...</div> : null}
            {error ? <div className="hv-ai-error">{error}</div> : null}
            <div ref={bottomRef} />
          </div>
          <form className="hv-ai-input-row" onSubmit={sendMessage}>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Destination, dates, guests..."
              aria-label="Message Homevilas travel concierge"
              disabled={aiOffline}
            />
            {canUseVoice ? (
              <button
                type="button"
                className={recording ? "hv-ai-voice-recording" : ""}
                onClick={recording ? stopRecording : startRecording}
                disabled={aiOffline || (loading && !recording)}
                aria-label={recording ? "Stop voice recording" : "Start voice recording"}
                title={hasBrowserSpeechRecognition ? (recording ? "Stop recording" : "Record voice") : "Voice works in Chrome or Edge"}
              >
                <i className={recording ? "bi bi-stop-fill" : "bi bi-mic-fill"} aria-hidden="true" />
              </button>
            ) : null}
            <button type="submit" disabled={!canSend} aria-label="Send message">
              <i className="bi bi-send-fill" aria-hidden="true" />
            </button>
          </form>
          {voiceReplyUrl ? (
            <div className="hv-ai-audio-row">
              <i className="bi bi-volume-up-fill" aria-hidden="true" />
              <audio src={voiceReplyUrl} controls />
            </div>
          ) : null}
        </div>
      ) : null}
      <button className="hv-ai-launcher" type="button" onClick={() => setOpen((value) => !value)} aria-label="Open Homevilas travel concierge">
        HV
      </button>
    </section>
  );
}
